import {
  CalEventStatus,
  EVENT_WORKSPACE_QUEUE_IDS,
  GENERIC_EVENT_TYPE_FILTER_VALUE,
  isCalendarDate,
  isValidUuidV7,
  MAX_UUID_ARRAY_LENGTH,
} from '@nudool/shared';

import {
  parseListRouteSearch,
  type ListRouteSearch,
} from '../../hooks/use-list-table-helpers/index.js';

import { parseMonthParam } from './calendar/month-param/index.js';
import { capEventFilterIds } from './cap-event-filter-ids.js';
import type { EventsView } from './events-view.js';
import type { EventWorkspaceQueue } from './events-workspace-queues.js';
import { isEventsView } from './is-events-view.js';

const KNOWN_STATUS_IDS = new Set<number>(
  Object.values(CalEventStatus).filter((value): value is number => typeof value === 'number'),
);

/** Caps template ids at MAX_UUID_ARRAY_LENGTH, keeping the Generic sentinel out of the count. */
export function capTypeValues(values: string[]): string[] {
  let templateIdCount = 0;
  return [...new Set(values)].filter((value) => {
    if (value === GENERIC_EVENT_TYPE_FILTER_VALUE) return true;
    templateIdCount += 1;
    return templateIdCount <= MAX_UUID_ARRAY_LENGTH;
  });
}

function parseCommaJoined<T>(raw: unknown, parse: (value: string) => T | undefined): T[] {
  if (typeof raw !== 'string' || raw.trim() === '') return [];
  return raw
    .split(',')
    .map((value) => value.trim())
    .filter((value) => value !== '')
    .map(parse)
    .filter((value): value is T => value !== undefined);
}

function parseUuidParam(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  return isValidUuidV7(trimmed) ? trimmed : undefined;
}

function parseUuidValue(value: string): string | undefined {
  return isValidUuidV7(value) ? value : undefined;
}

function parseTypeValue(value: string): string | undefined {
  return value === GENERIC_EVENT_TYPE_FILTER_VALUE ? value : parseUuidValue(value);
}

function parseIsoDateParam(raw: unknown): string | undefined {
  return isCalendarDate(raw) ? raw : undefined;
}

export type EventsListSearch = ListRouteSearch & {
  typeValues?: string;
  storeIds?: string;
  dateFrom?: string;
  dateTo?: string;
  statusIds?: string;
  brandIds?: string;
  assignedToMe?: boolean;
  assigneeId?: string;
  queue?: EventWorkspaceQueue;
  view?: EventsView;
  month?: string;
};

export function parseEventsListSearch(search: Record<string, unknown>): EventsListSearch {
  const parsed: EventsListSearch = { ...parseListRouteSearch(search) };

  const typeValues = capTypeValues(parseCommaJoined(search.typeValues, parseTypeValue));
  if (typeValues.length > 0) parsed.typeValues = typeValues.join(',');

  const storeIds = capEventFilterIds(parseCommaJoined(search.storeIds, parseUuidValue));
  if (storeIds.length > 0) parsed.storeIds = storeIds.join(',');

  const dateFrom = parseIsoDateParam(search.dateFrom);
  if (dateFrom) parsed.dateFrom = dateFrom;

  const dateTo = parseIsoDateParam(search.dateTo);
  if (dateTo) parsed.dateTo = dateTo;

  const statusIds = parseStatusIdsParam(search.statusIds);
  if (statusIds.length > 0) parsed.statusIds = statusIds.join(',');

  const brandIds = capEventFilterIds(parseCommaJoined(search.brandIds, parseUuidValue));
  // Absent means never touched and '' means explicitly cleared, so an empty or all-invalid result must still round-trip as ''.
  if (search.brandIds !== undefined) parsed.brandIds = brandIds.join(',');

  if (typeof search.assignedToMe === 'boolean') {
    parsed.assignedToMe = search.assignedToMe;
  } else if (search.assignedToMe === 'true' || search.assignedToMe === 'false') {
    parsed.assignedToMe = search.assignedToMe === 'true';
  }

  const assigneeId = parseUuidParam(search.assigneeId);
  if (assigneeId) parsed.assigneeId = assigneeId;

  if (
    typeof search.queue === 'string' &&
    (EVENT_WORKSPACE_QUEUE_IDS as readonly string[]).includes(search.queue)
  ) {
    parsed.queue = search.queue as EventWorkspaceQueue;
  }

  if (isEventsView(search.view)) parsed.view = search.view;

  // A fixed fallback proves validity without depending on today's date or timezone.
  if (
    typeof search.month === 'string' &&
    parseMonthParam(search.month, new Date(2000, 0, 1)) === search.month
  ) {
    parsed.month = search.month;
  }

  return parsed;
}

export function parseStatusIdsParam(raw?: unknown): number[] {
  return capEventFilterIds(
    parseCommaJoined(raw, (value) => {
      const statusId = Number(value);
      return Number.isInteger(statusId) && KNOWN_STATUS_IDS.has(statusId) ? statusId : undefined;
    }),
  );
}
